import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { Form, Input, Button, Select, message, Row, Col, Divider, Card } from 'antd';
import {
  SaveOutlined,
  CloseOutlined,
  ShopOutlined,
  UserOutlined,
  PhoneOutlined,
  EnvironmentOutlined,
  IdcardOutlined,
  InfoCircleOutlined
} from '@ant-design/icons';
import { createVendor, updateVendor, fetchVendor } from '../../redux/actions/vendorActions';
import { udhyamCategoriesAPI } from '../../api/config';
import './Vendor.css';
import '../../styles/forms.css';

const { Option } = Select;

const VendorForm = () => {
  const [form] = Form.useForm();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { id } = useParams();
  const [initialValues, setInitialValues] = useState(null);
  const [udhyamCategories, setUdhyamCategories] = useState([]);
  const { currentVendor, error } = useSelector((state) => state.vendors);

  useEffect(() => {
    if (error) {
      message.error(error);
    }
  }, [error]);
console.log("tjhe value of udhayamcategories is ", udhyamCategories);

  useEffect(() => {
    // Fetch Udhyam categories
    const fetchCategories = async () => {
      try {
        const response = await udhyamCategoriesAPI.fetchUdhyamCategories();
        console.log(
          "the value of resposnse is ",
          response.data.udhyam_categories
        );

        let categories = [];
        if (response.data.udhyam_categories) {
          categories = response.data.udhyam_categories.map(cat => {
            if (typeof cat === 'string') {
              return { value: cat, label: cat };
            } else {
              return { value: cat.category_name, label: cat.category_name };
            }
          });
        }
        setUdhyamCategories(categories);
      } catch (error) {
        console.error('Error fetching Udhyam categories:', error);
        // Fallback categories
        setUdhyamCategories([
          { value: 'Micro Enterprise', label: 'Micro Enterprise' },
          { value: 'Small Enterprise', label: 'Small Enterprise' },
          { value: 'Medium Enterprise', label: 'Medium Enterprise' }
        ]);
      }
    };
    fetchCategories();
  }, []);

  useEffect(() => {
    if (id) {
      // If editing, fetch vendor details
      dispatch(fetchVendor(id));
    }
  }, [id, dispatch]);

  useEffect(() => {
    if (currentVendor && id) {
      // Set form values when vendor data is loaded
      const formValues = {
        firmName: currentVendor.firm_name || '',
        contactName: currentVendor.contact_name || '',
        mobile: currentVendor.mobile || '',
        address: currentVendor.address || '',
        gstNumber: currentVendor.gst_number || '',
        udhyamNumber: currentVendor.udhyam_number || '',
        udhyamCategory: currentVendor.udhyam_category || undefined
      };
      setInitialValues(formValues);
      form.setFieldsValue(formValues);
    }
  }, [currentVendor, id, form]);

  const onFinish = (values) => {
    const vendorData = {
      firm_name: values.firmName,
      contact_name: values.contactName,
      mobile: values.mobile,
      address: values.address,
      gst_number: values.gstNumber?.toUpperCase() || '',
      udhyam_number: values.udhyamNumber?.toUpperCase() || '',
      udhyam_category: values.udhyamCategory
    };

    if (id) {
      dispatch(updateVendor(id, vendorData));
    } else {
      dispatch(createVendor(vendorData));
    }
    navigate('/vendors');
  };

  const validateMobileNumber = (_, value) => {
    if (!value) {
      return Promise.reject('Please enter mobile number');
    }
    if (!/^\d{10}$/.test(value)) {
      return Promise.reject('Please enter a valid 10-digit mobile number');
    }
    return Promise.resolve();
  };

  const validateGSTNumber = (_, value) => {
    if (!value) {
      return Promise.resolve(); // GST is optional
    }
    // Updated GST validation pattern: 2 digits + 5 letters + 4 digits + 1 letter + 1 alphanumeric + Z + 1 alphanumeric
    if (!/^\d{2}[A-Z]{5}\d{4}[A-Z]{1}[A-Z\d]{1}[Z]{1}[A-Z\d]{1}$/.test(value.toUpperCase())) {
      return Promise.reject('Invalid GST number format. Expected format: 22AAAAA0000A1Z5');
    }
    return Promise.resolve();
  };

  const validateUdhyamNumber = (_, value) => {
    if (!value) {
      return Promise.resolve(); // Udhyam is optional
    }
    // Udhyam validation pattern: UDYAM- + 2 letters + - + 2 digits + - + 7 digits
    if (!/^UDYAM-[A-Z]{2}-\d{2}-\d{7}$/.test(value.toUpperCase())) {
      return Promise.reject('Invalid Udhyam number format. Expected format: UDYAM-XX-00-0000000');
    }
    return Promise.resolve();
  };

  return (
    <div className="page-container">
      <div className="page-content">
        <Card className="professional-form" style={{ maxWidth: 900, margin: '0 auto' }}>
          {/* Form Header */}
          <div className="form-modal-header" style={{ borderBottom: '2px solid #f0f0f0' }}>
            <div className="form-modal-icon">
              <ShopOutlined />
            </div>
            <div className="form-modal-title">
              <h3>{id ? 'Edit Vendor Details' : 'Add New Vendor'}</h3>
              <p>Register vendor information for purchase orders</p>
            </div>
          </div>

          <Form
            form={form}
            layout="vertical"
            initialValues={initialValues}
            onFinish={onFinish}
            style={{ marginTop: '1.5rem' }}
            requiredMark="optional"
          >
            {/* Basic Information */}
            <Row gutter={16}>
              <Col xs={24}>
                <Form.Item
                  name="firmName"
                  label="Firm / Company Name"
                  rules={[{ required: true, message: 'Please enter firm name' }]}
                  tooltip="Official business name of the vendor"
                >
                  <Input
                    prefix={<ShopOutlined style={{ color: '#999' }} />}
                    placeholder="e.g., ABC Suppliers Pvt Ltd, XYZ Enterprises"
                    size="large"
                  />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={16}>
              <Col xs={24} sm={12}>
                <Form.Item
                  name="contactName"
                  label="Contact Person (K/A Name)"
                  rules={[{ required: true, message: 'Please enter contact name' }]}
                  tooltip="Primary contact person at vendor firm"
                >
                  <Input
                    prefix={<UserOutlined style={{ color: '#999' }} />}
                    placeholder="Enter contact person name"
                    size="large"
                  />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12}>
                <Form.Item
                  name="mobile"
                  label="Mobile Number"
                  rules={[{ validator: validateMobileNumber }]}
                  tooltip="10-digit mobile number"
                >
                  <Input
                    prefix={<PhoneOutlined style={{ color: '#999' }} />}
                    maxLength={10}
                    placeholder="9876543210"
                    style={{ fontFamily: 'monospace' }}
                    size="large"
                  />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={16}>
              <Col xs={24}>
                <Form.Item
                  name="address"
                  label="Complete Address"
                  rules={[{ required: true, message: 'Please enter address' }]}
                >
                  <Input.TextArea
                    rows={3}
                    placeholder="Enter complete business address with city, state, and pincode"
                    showCount
                    maxLength={500}
                    size="large"
                  />
                </Form.Item>
              </Col>
            </Row>

            <Divider orientation="left" style={{ margin: '1.5rem 0 1rem' }}>
              <span style={{ fontSize: '0.85rem', color: '#666' }}>Registration Details (Optional)</span>
            </Divider>

            <Row gutter={16}>
              <Col xs={24} sm={12}>
                <Form.Item
                  name="gstNumber"
                  label="GST Number"
                  rules={[{ validator: validateGSTNumber }]}
                  tooltip="15-digit GST identification number"
                >
                  <Input
                    prefix={<IdcardOutlined style={{ color: '#999' }} />}
                    maxLength={15}
                    style={{ textTransform: 'uppercase', fontFamily: 'monospace' }}
                    placeholder="22AAAAA0000A1Z5"
                    size="large"
                  />
                </Form.Item>
                <div className="input-help-text" style={{ marginTop: '-0.5rem', marginBottom: '1rem' }}>
                  <InfoCircleOutlined />
                  Format: 2 digits + 5 letters + 4 digits + 1 letter + 1 char + Z + 1 char
                </div>
              </Col>
              <Col xs={24} sm={12}>
                <Form.Item
                  name="udhyamCategory"
                  label="Udhyam Category"
                  tooltip="Business classification category"
                >
                  <Select
                    placeholder="Select category"
                    allowClear
                    size="large"
                  >
                    {udhyamCategories.map(category => (
                      <Option key={category.value} value={category.value}>
                        {category.label}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={16}>
              <Col xs={24}>
                <Form.Item
                  name="udhyamNumber"
                  label="Udhyam Registration Number"
                  rules={[{ validator: validateUdhyamNumber }]}
                  tooltip="MSME registration number"
                >
                  <Input
                    prefix={<IdcardOutlined style={{ color: '#999' }} />}
                    style={{ textTransform: 'uppercase', fontFamily: 'monospace' }}
                    placeholder="UDYAM-XX-00-0000000"
                    size="large"
                  />
                </Form.Item>
                <div className="input-help-text" style={{ marginTop: '-0.5rem', marginBottom: '1rem' }}>
                  <InfoCircleOutlined />
                  Format: UDYAM- + 2 letters + - + 2 digits + - + 7 digits
                </div>
              </Col>
            </Row>

            <div className="form-actions" style={{ marginTop: '2rem' }}>
              <Button onClick={() => navigate('/vendors')} size="large">
                <CloseOutlined />
                Cancel
              </Button>
              <Button
                type="primary"
                htmlType="submit"
                icon={<SaveOutlined />}
                size="large"
              >
                {id ? 'Update Vendor' : 'Create Vendor'}
              </Button>
            </div>
          </Form>
        </Card>
      </div>
    </div>
  );
};

export default VendorForm;